import { memo, useMemo } from 'react'
import { faCrown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  faBadgePercent,
  faBullseyeArrow, faCalendarCheck, faCalendarDays, faCalendarXmark, faCrosshairs, faDownload, faTally, faTasks, faUser,
  faWarning,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, DashboardModule, Table, Text, UserProfileImage,
} from '@domain-group/suit-theme'

import useChartData from '@hooks/useChartData'
import useReporting from '@hooks/useReporting'

import handleCallbackAction from '@functions/handleCallbackAction'

import { getDateRange } from '@functions/reporting'

import AnalyticsBlankstateImageUrl from '../../../assets/analytics.svg'

import TableKeys from './TableKeys'

export const icon = faTasks

export const configOptions = () => ({
  title: {
    component: 'input',
    key: 'title',
    label: 'Title',
    optionKey: 'customization',
  },
  month: {
    component: 'select',
    key: 'month',
    label: 'Month',
    optionKey: 'queryParams',
    values: [
      { key: 'current_month', label: 'Current Month' },
      { key: 'previous_month', label: 'Previous Month' },
    ],
  },
  limit: {
    component: 'select',
    key: 'limit',
    label: 'Limit',
    optionKey: 'queryParams',
    values: [
      { key: 5, label: '5' },
      { key: 10, label: '10' },
      { key: 25, label: '25' },
      { key: 50, label: '50' },
    ],
  },
})

const buildColumns = (leadingUserId: number) => [
  {
    title: 'Team Member',
    dataKey: 'assignee_name',
    cellProps: {
      textAlign: 'center',
    },
    render: (assignee_name: string, data: any) => (
      <Box flexDirection="row" alignItems="center">
        <Box flexDirection="column" width="auto" alignItems="center" position="relative">
          {data.assignee_id === leadingUserId && (
            <Text
              color="gold"
              style={{
                left: 1, position: 'absolute', bottom: 26, transform: 'rotate(-12deg)',
              }}
            >
              <FontAwesomeIcon icon={faCrown} />
            </Text>
          )}

          <UserProfileImage imageUrl={data.assignee_image_url}>
            <FontAwesomeIcon icon={faUser} />
          </UserProfileImage>
        </Box>

        <Box marginLeft="medium">
          <Text fontSize="small">{assignee_name}</Text>
        </Box>
      </Box>
    ),
  },
  {
    title: <FontAwesomeIcon icon={faTally} />,
    dataKey: 'total_completed',
    cellProps: {
      textAlign: 'center',
    },
    render: (total_completed: number) => (
      <Text>{total_completed}</Text>
    ),
  },
  {
    title: <FontAwesomeIcon icon={faCalendarCheck} />,
    dataKey: 'total_of_tasks_within_sla',
    cellProps: {
      textAlign: 'center',
    },
    render: (total_of_tasks_within_sla: number) => (
      <Text>{total_of_tasks_within_sla}</Text>
    ),
  },
  {
    title: <FontAwesomeIcon icon={faCalendarXmark} />,
    dataKey: 'total_of_tasks_outside_sla',
    cellProps: {
      textAlign: 'center',
    },
    render: (total_of_tasks_outside_sla: number) => (
      <Text>{total_of_tasks_outside_sla}</Text>
    ),
  },
  {
    title: <FontAwesomeIcon icon={faCalendarDays} />,
    dataKey: 'sla_percent',
    cellProps: {
      textAlign: 'center',
    },
    render: (sla_percent: number) => (
      <Text>{sla_percent}&#37;</Text>
    ),
  },
  {
    title: <FontAwesomeIcon icon={faBullseyeArrow} />,
    dataKey: 'total_first_time_right',
    cellProps: {
      textAlign: 'center',
    },
    render: (total_first_time_right: number) => (
      <Text>{total_first_time_right}</Text>
    ),
  },
  {
    title: <FontAwesomeIcon icon={faWarning} />,
    dataKey: 'total_jobs_with_qc_errors',
    cellProps: {
      textAlign: 'center',
    },
    render: (total_jobs_with_qc_errors: number) => (
      <Text>{total_jobs_with_qc_errors}</Text>
    ),
  },
  {
    title: <FontAwesomeIcon icon={faBadgePercent} />,
    dataKey: 'percentage_first_time_right',
    cellProps: {
      textAlign: 'center',
    },
    render: (percentage_first_time_right: number) => (
      <Text>{percentage_first_time_right}&#37;</Text>
    ),
  },
  {
    title: <FontAwesomeIcon icon={faCrosshairs} />,
    dataKey: 'qc_accuracy_rating',
    cellProps: {
      textAlign: 'center',
    },
    render: (qc_accuracy_rating: number) => (
      <Text>{qc_accuracy_rating}&#37;</Text>
    ),
  },
]

interface LeaderboardTableProps {
  customOptions: {
    customization?: {
      title?: string,
    },
    queryParams?: object,
  },
  editing: boolean,
}

const LeaderboardTable = (props: LeaderboardTableProps) => {
  const { customOptions, editing } = props
  const { queryParams } = customOptions || {}

  const title = customOptions?.customization?.title

  const dataPayload = useChartData('first_time_right_leaderboard', { requestOptions: queryParams })

  const {
    chartDataKey, dataArray, hasData, loading,
  } = dataPayload

  const leadingUserId = dataArray[0]?.assignee_id || 0

  // @ts-expect-error TS2339
  const memoColumns = useMemo(() => buildColumns(leadingUserId), [queryParams?.month, dataArray.length])

  // @ts-expect-error TS2339
  const { endDate, startDate } = getDateRange(queryParams?.month)

  const reportingPayload = useReporting({
    // @ts-expect-error TS2322
    allowedReports: ['first_time_right_leaderboard'],
    defaultValues: { chart_data_key: chartDataKey, end_date: endDate, start_date: startDate },
    formDeps: [chartDataKey, endDate, startDate],
  })

  const {
    callbacks: {
      generateReport: generateFn,
    },
    loading: loadingReport,
    saveEnabled,
  } = reportingPayload

  const generateReportPayload = {
    callbacks: {
      action: generateFn,
    },
    toastText: 'Report Queued',
  }

  return (
    <DashboardModule
      boxProps={{ margin: 'none' }}
      contentBoxProps={{ flexDirection: 'column' }}
      editing={editing}
      title={title || 'Task Accuracy Leaderboard'}
    >
      <TableKeys />

      <Table
        boxProps={{
          borderBottom: 'none',
          borderRadius: 'none',
          borderX: 'none',
          maxHeight: 800,
          overflowY: hasData ? 'auto' : '',
        }}
        columns={memoColumns}
        data={dataArray}
        // @ts-expect-error TS2322
        scroll={{ x: '100%' }}
        stickyHeader
      />

      {!dataArray.length && !loading && (
        <DashboardModule.BlankState imageUrl={AnalyticsBlankstateImageUrl}>
          <DashboardModule.BlankState.Title>No Leaderboard Available</DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>Try adjusting your filters.</DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      )}

      {hasData && (
        <DashboardModule.Footer>
          <Button
            buttonStyle="secondaryEdit"
            disabled={!saveEnabled}
            icon={<FontAwesomeIcon icon={faDownload} />}
            // @ts-expect-error TS2345
            onClick={() => handleCallbackAction(generateReportPayload)}
            loading={loadingReport}
            marginLeft="auto"
            // @ts-expect-error TS2322
            size={['medium', 'small']}
            width={['100%', 'fit-content']}
          >
            Download CSV
          </Button>
        </DashboardModule.Footer>
      )}
    </DashboardModule>
  )
}

export default memo(LeaderboardTable)
